import React, { useEffect, useState } from "react";
import { database } from "../../firebaseConfig";
import { ref, get, set as dbSet, push, remove } from "firebase/database";
import RichTextField from "./ValueBasedOffer/RichTextField";
import "./Terms.css";

/**
 * Terms library
 * -------------
 * Reusable contract-terms blocks (e.g. "Partnerschaftliche Zusammenarbeit",
 * "Zahlungsbedingungen / Gültigkeit", "Geheimhaltung", ...) that the user
 * can pre-select inside the Value-Based Offer configurator. The selected
 * terms are then automatically appended to every generated offer above the
 * representative-signature block; the user can still add or remove blocks
 * manually inside the OfferView in the same style.
 *
 * RTDB shape:
 *   /World/<userId>/terms/<termId> = {
 *     title: "Partnerschaftliche Zusammenarbeit",
 *     html:  "<p>Sie haben jederzeit ...</p>",
 *     enabledByDefault: true,         // pre-checked in the configurator
 *     order: 0,                        // manual ordering for default attach
 *     createdAt: 0,
 *     updatedAt: 0
 *   }
 */

const sanitize = (val) => {
  if (Array.isArray(val)) return val.map(sanitize);
  if (val && typeof val === "object") {
    const out = {};
    for (const k of Object.keys(val)) {
      const v = sanitize(val[k]);
      if (v !== undefined) out[k] = v;
    }
    return out;
  }
  return val === undefined ? null : val;
};

const stripTags = (html) =>
  String(html || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const Terms = ({ userId }) => {
  const [terms, setTerms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({
    title: "",
    html: "",
    enabledByDefault: true,
  });
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const snap = await get(ref(database, `/World/${userId}/terms`));
        if (cancelled) return;
        if (!snap.exists()) {
          setTerms([]);
        } else {
          const list = [];
          snap.forEach((child) => {
            list.push({ id: child.key, ...child.val() });
          });
          list.sort(
            (a, b) =>
              (a.order ?? 9999) - (b.order ?? 9999) ||
              (a.createdAt || 0) - (b.createdAt || 0)
          );
          setTerms(list);
        }
      } catch (e) {
        console.error("Terms: load failed", e);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const flash = (message, type = "success") => {
    setNotice({ message, type });
    window.clearTimeout(flash._t);
    flash._t = window.setTimeout(() => setNotice(null), 2400);
  };

  const startCreate = () => {
    setEditingId("__new__");
    setDraft({ title: "", html: "", enabledByDefault: true });
  };

  const startEdit = (term) => {
    setEditingId(term.id);
    setDraft({
      title: term.title || "",
      html: term.html || "",
      enabledByDefault: term.enabledByDefault !== false,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft({ title: "", html: "", enabledByDefault: true });
  };

  const save = async () => {
    if (!userId) return;
    const title = draft.title.trim();
    const html = String(draft.html || "").trim();
    if (!title) {
      flash("Titel fehlt", "error");
      return;
    }
    try {
      if (editingId === "__new__") {
        const listRef = ref(database, `/World/${userId}/terms`);
        const newRef = push(listRef);
        const payload = sanitize({
          title,
          html,
          enabledByDefault: draft.enabledByDefault !== false,
          order: terms.length,
          createdAt: Date.now(),
          updatedAt: Date.now(),
        });
        await dbSet(newRef, payload);
        setTerms((cur) => [...cur, { id: newRef.key, ...payload }]);
      } else {
        const existing = terms.find((t) => t.id === editingId);
        const payload = sanitize({
          title,
          html,
          enabledByDefault: draft.enabledByDefault !== false,
          order: existing?.order ?? terms.length,
          createdAt: existing?.createdAt || Date.now(),
          updatedAt: Date.now(),
        });
        await dbSet(
          ref(database, `/World/${userId}/terms/${editingId}`),
          payload
        );
        setTerms((cur) =>
          cur.map((t) => (t.id === editingId ? { id: editingId, ...payload } : t))
        );
      }
      flash("Gespeichert");
      cancelEdit();
    } catch (e) {
      console.error("Terms: save failed", e);
      flash("Fehler beim Speichern", "error");
    }
  };

  const removeTerm = async (term) => {
    if (!userId) return;
    if (!window.confirm(`„${term.title}" wirklich löschen?`)) return;
    try {
      await remove(ref(database, `/World/${userId}/terms/${term.id}`));
      setTerms((cur) => cur.filter((t) => t.id !== term.id));
      flash("Gelöscht");
      if (editingId === term.id) cancelEdit();
    } catch (e) {
      console.error("Terms: delete failed", e);
      flash("Fehler beim Löschen", "error");
    }
  };

  const move = async (term, dir) => {
    const idx = terms.findIndex((t) => t.id === term.id);
    const newIdx = idx + dir;
    if (idx < 0 || newIdx < 0 || newIdx >= terms.length) return;
    const next = [...terms];
    const [moved] = next.splice(idx, 1);
    next.splice(newIdx, 0, moved);
    setTerms(next);
    try {
      await Promise.all(
        next.map((t, i) =>
          dbSet(
            ref(database, `/World/${userId}/terms/${t.id}/order`),
            i
          )
        )
      );
    } catch (e) {
      console.error("Terms: reorder failed", e);
    }
  };

  const toggleDefault = async (term) => {
    const nextVal = !(term.enabledByDefault !== false);
    setTerms((cur) =>
      cur.map((t) =>
        t.id === term.id ? { ...t, enabledByDefault: nextVal } : t
      )
    );
    try {
      await dbSet(
        ref(database, `/World/${userId}/terms/${term.id}/enabledByDefault`),
        nextVal
      );
    } catch (e) {
      console.error("Terms: toggle default failed", e);
    }
  };

  if (isLoading) {
    return <div className="terms-page__loading">Lade Terms…</div>;
  }

  return (
    <div className="terms-page">
      <header className="terms-page__head">
        <div>
          <h1 className="terms-page__title">Terms-Bibliothek</h1>
          <p className="terms-page__sub">
            Lege wiederverwendbare Vertragsbedingungen an (z. B.
            „Partnerschaftliche Zusammenarbeit", „Zahlungsbedingungen",
            „Geheimhaltung"). Im Value-Based-Konfigurator wählst du dann nur
            noch aus, welche dieser Terms automatisch ans Angebot angehängt
            werden — manuell ergänzen geht im Editor weiterhin nahtlos.
          </p>
        </div>
        <button
          type="button"
          className="terms-page__btn terms-page__btn--primary"
          onClick={startCreate}
          disabled={editingId === "__new__"}
        >
          + Neuer Term
        </button>
      </header>

      {notice && (
        <div className={`terms-page__notice terms-page__notice--${notice.type}`}>
          {notice.message}
        </div>
      )}

      {editingId && (
        <div className="terms-page__editor">
          <div className="terms-page__editor-head">
            <h2 className="terms-page__editor-title">
              {editingId === "__new__" ? "Neuer Term" : "Term bearbeiten"}
            </h2>
            <div className="terms-page__editor-actions">
              <button
                type="button"
                className="terms-page__btn"
                onClick={cancelEdit}
              >
                Abbrechen
              </button>
              <button
                type="button"
                className="terms-page__btn terms-page__btn--primary"
                onClick={save}
              >
                Speichern
              </button>
            </div>
          </div>
          <label className="terms-page__field">
            <span className="terms-page__field-label">Titel</span>
            <input
              type="text"
              className="terms-page__input"
              value={draft.title}
              placeholder="z. B. Partnerschaftliche Zusammenarbeit"
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              autoFocus
            />
          </label>
          <label className="terms-page__field">
            <span className="terms-page__field-label">Inhalt</span>
            <RichTextField
              value={draft.html}
              onChange={(html) => setDraft({ ...draft, html })}
              minHeight={200}
              placeholder="Inhalt des Terms… (Formatierung via ⌘B / ⌘I / ⌘U)"
            />
          </label>
          <label className="terms-page__checkbox">
            <input
              type="checkbox"
              checked={draft.enabledByDefault !== false}
              onChange={(e) =>
                setDraft({ ...draft, enabledByDefault: e.target.checked })
              }
            />
            <span>
              Im Value-Based-Konfigurator standardmäßig vorausgewählt
              <em>
                Wenn aktiv, ist dieser Term beim Erstellen eines neuen
                Value-Based-Offers automatisch hinzugefügt — du kannst ihn
                im Konfigurator pro Angebot weiterhin abwählen.
              </em>
            </span>
          </label>
        </div>
      )}

      {terms.length === 0 && !editingId && (
        <div className="terms-page__empty">
          Noch keine Terms angelegt. Klicke oben rechts auf
          „<strong>+ Neuer Term</strong>", um deine erste wiederverwendbare
          Klausel zu speichern.
        </div>
      )}

      <ul className="terms-page__list">
        {terms.map((term, i) => {
          const isEditing = editingId === term.id;
          const preview = stripTags(term.html);
          return (
            <li
              key={term.id}
              className={`terms-page__row ${isEditing ? "is-editing" : ""}`}
            >
              <div className="terms-page__row-main">
                <div className="terms-page__row-title">
                  <strong>{term.title || "(ohne Titel)"}</strong>
                  {term.enabledByDefault !== false && (
                    <span className="terms-page__badge">Default</span>
                  )}
                </div>
                {preview && (
                  <div className="terms-page__row-preview">
                    {preview.length > 220
                      ? preview.slice(0, 220) + "…"
                      : preview}
                  </div>
                )}
              </div>
              <div className="terms-page__row-actions">
                <button
                  type="button"
                  className="terms-page__icon-btn"
                  title="Nach oben"
                  disabled={i === 0}
                  onClick={() => move(term, -1)}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="terms-page__icon-btn"
                  title="Nach unten"
                  disabled={i === terms.length - 1}
                  onClick={() => move(term, +1)}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="terms-page__icon-btn"
                  title={
                    term.enabledByDefault !== false
                      ? "Default-Auswahl deaktivieren"
                      : "Standardmäßig im Angebot mitführen"
                  }
                  onClick={() => toggleDefault(term)}
                >
                  {term.enabledByDefault !== false ? "★" : "☆"}
                </button>
                <button
                  type="button"
                  className="terms-page__btn terms-page__btn--ghost"
                  onClick={() => startEdit(term)}
                >
                  Bearbeiten
                </button>
                <button
                  type="button"
                  className="terms-page__btn terms-page__btn--danger"
                  onClick={() => removeTerm(term)}
                >
                  Löschen
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default Terms;
